import { message, Modal } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getOrgRoleAssignOptions, saveOrgRoleConfig } from "@/api/employees";
import type { ShopRoleBriefVO } from "@/api/employees/type";

import {
  collectRoleNames,
  filterRolesByKeyword,
  getRoleKey,
  isRoleSelectionDirty,
} from "../components/EmployeeRoleAssignModal/transferUtils";

/** 打开角色分配弹窗的员工上下文 */
export interface EmployeeRoleAssignTarget {
  /** 员工档案主键 */
  employeeId: string;
  /** 组织 ID */
  orgId: string;
  /** 员工姓名（副标题展示） */
  employeeName?: string;
  /** 员工工号（副标题展示） */
  employeeNo?: string;
}

export interface UseEmployeeRoleAssignModalParams {
  /** 弹窗是否打开 */
  open: boolean;
  /** 当前员工上下文 */
  target: EmployeeRoleAssignTarget | null;
  /** 关闭弹窗 */
  onClose: () => void;
  /** 保存成功后刷新列表 */
  onSuccess?: () => void;
}

/**
 * 员工角色分配弹窗：左右两份角色列表，箭头互移，本侧独立搜索
 */
export function useEmployeeRoleAssignModal({
  open,
  target,
  onClose,
  onSuccess,
}: UseEmployeeRoleAssignModalParams) {
  // 左侧：可分配角色
  const [leftRoles, setLeftRoles] = useState<ShopRoleBriefVO[]>([]);
  // 右侧：已分配角色
  const [rightRoles, setRightRoles] = useState<ShopRoleBriefVO[]>([]);
  // 打开时右侧角色名，用于判断 dirty
  const [initialRightNames, setInitialRightNames] = useState<string[]>([]);
  // 左右勾选（角色名）
  const [selectedLeftKeys, setSelectedLeftKeys] = useState<string[]>([]);
  const [selectedRightKeys, setSelectedRightKeys] = useState<string[]>([]);
  // 左右独立搜索词
  const [leftKeyword, setLeftKeyword] = useState("");
  const [rightKeyword, setRightKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /** 重置勾选与搜索 */
  const resetSelections = useCallback(() => {
    setSelectedLeftKeys([]);
    setSelectedRightKeys([]);
    setLeftKeyword("");
    setRightKeyword("");
  }, []);

  // 打开时按员工拉取左右两侧数据
  useEffect(() => {
    if (!open || !target?.employeeId) {
      setLeftRoles([]);
      setRightRoles([]);
      setInitialRightNames([]);
      resetSelections();
      setLoading(false);
      setSaving(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    resetSelections();
    getOrgRoleAssignOptions(target.employeeId, {
      orgId: target.orgId || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const available = res.data?.availableRoles ?? [];
        const assigned = res.data?.assignedRoles ?? [];
        setLeftRoles(available);
        setRightRoles(assigned);
        setInitialRightNames(collectRoleNames(assigned));
      })
      .catch(() => {
        if (!cancelled) {
          message.error("加载角色配置失败");
          onClose();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, target, onClose, resetSelections]);

  // 本侧搜索只过滤本侧列表
  const filteredLeftRoles = useMemo(
    () => filterRolesByKeyword(leftRoles, leftKeyword),
    [leftRoles, leftKeyword],
  );
  const filteredRightRoles = useMemo(
    () => filterRolesByKeyword(rightRoles, rightKeyword),
    [rightRoles, rightKeyword],
  );

  const rightRoleNames = useMemo(() => collectRoleNames(rightRoles), [rightRoles]);
  const isDirty = useMemo(
    () => isRoleSelectionDirty(initialRightNames, rightRoleNames),
    [initialRightNames, rightRoleNames],
  );

  /** 左右侧单个勾选 */
  const toggleSelection = useCallback(
    (roleName: string, checked: boolean, side: "left" | "right") => {
      const setter = side === "left" ? setSelectedLeftKeys : setSelectedRightKeys;
      setter((prev) =>
        checked
          ? prev.includes(roleName)
            ? prev
            : [...prev, roleName]
          : prev.filter((name) => name !== roleName),
      );
    },
    [],
  );

  /** 左右侧全选当前可见列表 */
  const toggleSelectAll = useCallback(
    (checked: boolean, items: ShopRoleBriefVO[], side: "left" | "right") => {
      const itemKeys = collectRoleNames(items);
      const setter = side === "left" ? setSelectedLeftKeys : setSelectedRightKeys;
      if (checked) {
        setter((prev) => [...new Set([...prev, ...itemKeys])]);
        return;
      }
      const removeSet = new Set(itemKeys);
      setter((prev) => prev.filter((name) => !removeSet.has(name)));
    },
    [],
  );

  /** →：把左侧勾选移到右侧 */
  const moveToAssigned = useCallback(() => {
    if (!selectedLeftKeys.length) return;
    const selectedSet = new Set(selectedLeftKeys);
    const moving = leftRoles.filter((item) => {
      const key = getRoleKey(item);
      return key != null && selectedSet.has(key);
    });
    if (!moving.length) return;
    setLeftRoles((prev) =>
      prev.filter((item) => {
        const key = getRoleKey(item);
        return key == null || !selectedSet.has(key);
      }),
    );
    setRightRoles((prev) => {
      const existKeys = new Set(collectRoleNames(prev));
      const append = moving.filter((item) => {
        const key = getRoleKey(item);
        return key != null && !existKeys.has(key);
      });
      return [...prev, ...append];
    });
    setSelectedLeftKeys([]);
  }, [leftRoles, selectedLeftKeys]);

  /** ←：把右侧勾选移回左侧 */
  const moveToAvailable = useCallback(() => {
    if (!selectedRightKeys.length) return;
    const selectedSet = new Set(selectedRightKeys);
    const moving = rightRoles.filter((item) => {
      const key = getRoleKey(item);
      return key != null && selectedSet.has(key);
    });
    if (!moving.length) return;
    setRightRoles((prev) =>
      prev.filter((item) => {
        const key = getRoleKey(item);
        return key == null || !selectedSet.has(key);
      }),
    );
    setLeftRoles((prev) => {
      const existKeys = new Set(collectRoleNames(prev));
      const append = moving.filter((item) => {
        const key = getRoleKey(item);
        return key != null && !existKeys.has(key);
      });
      return [...prev, ...append];
    });
    setSelectedRightKeys([]);
  }, [rightRoles, selectedRightKeys]);

  /** 保存：提交当前右侧角色名全量覆盖 */
  const handleSave = useCallback(async () => {
    if (!target?.employeeId) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await saveOrgRoleConfig(
        target.employeeId,
        { roleNames: rightRoleNames },
        { orgId: target.orgId || undefined },
      );
      message.success("角色配置已保存");
      onSuccess?.();
      onClose();
    } catch {
      message.error("保存角色配置失败");
    } finally {
      setSaving(false);
    }
  }, [isDirty, onClose, onSuccess, rightRoleNames, target]);

  /** 关闭：有未保存变更时二次确认 */
  const requestClose = useCallback(() => {
    if (!isDirty) {
      onClose();
      return;
    }
    Modal.confirm({
      title: "放弃未保存的修改？",
      content: "当前有角色配置的未保存变更，确认要关闭弹窗吗？",
      okText: "放弃修改",
      cancelText: "继续编辑",
      okButtonProps: { danger: true },
      onOk: () => {
        onClose();
      },
    });
  }, [isDirty, onClose]);

  const displayName =
    target?.employeeName?.trim() || target?.employeeNo?.trim() || target?.employeeId || "-";

  return {
    loading,
    saving,
    displayName,
    isDirty,
    availableRoles: leftRoles,
    assignedRoles: rightRoles,
    filteredAvailableRoles: filteredLeftRoles,
    filteredAssignedRoles: filteredRightRoles,
    selectedLeftKeys,
    selectedRightKeys,
    leftKeyword,
    rightKeyword,
    setLeftKeyword,
    setRightKeyword,
    toggleSelection,
    toggleSelectAll,
    moveToAssigned,
    moveToAvailable,
    handleSave,
    requestClose,
  };
}
